import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowLeft } from "lucide-react";
import { NAV, BRAND, ASSETS } from "@/content/site";

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Close menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Escape + body scroll lock for mobile menu
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  const solid = true;
  const textClass = "text-white";

  const goDiagnose = () => navigate("/discover-context");

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 transition-colors duration-500"
      style={{ backgroundColor: solid ? "var(--navy)" : "transparent" }}
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 h-[76px] flex items-center justify-between">
        {/* Logo */}
        <Link to="/" data-testid="logo-home-link" aria-label="سياق — الرئيسية" className="inline-flex items-center py-2 -my-2">
          <img
            src={ASSETS.logo}
            alt="سياق"
            width="120"
            height="44"
            className="h-11 w-auto transition-[filter] duration-500"
            style={solid ? { filter: "brightness(0) invert(1)" } : undefined}
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-5" aria-label="التنقل الرئيسي">
          {NAV.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              data-testid={`nav-${item.href.replace("/", "")}`}
              className={`link-underline text-[0.88rem] font-medium ${textClass} ${
                location.pathname === item.href ? "text-coral" : ""
              }`}
            >
              {item.label}
            </Link>
          ))}
          <button
            onClick={goDiagnose}
            data-testid="header-cta"
            className="group inline-flex items-center gap-2 bg-coral text-white px-6 py-3 text-[0.95rem] font-semibold hover:bg-[#d85a5e] transition-colors"
            style={{ backgroundColor: "var(--coral)" }}
          >
            ابدأ تشخيص منظمتك
            <ArrowLeft size={17} className="transition-transform group-hover:-translate-x-1" />
          </button>
        </nav>

        {/* Mobile toggle */}
        <button
          className={`lg:hidden inline-flex items-center justify-center p-2.5 -m-2.5 ${textClass}`}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? "إغلاق القائمة" : "فتح القائمة"}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          id="mobile-menu"
          data-testid="mobile-menu"
          className="lg:hidden fixed inset-0 top-[76px] z-40 bg-navy px-6 py-10 flex flex-col"
          style={{ backgroundColor: "var(--navy)", height: "calc(100dvh - 76px)" }}
          role="dialog"
          aria-modal="true"
          aria-label="القائمة"
        >
          <nav className="flex flex-col gap-2" aria-label="التنقل للجوال">
            {NAV.map((item, i) => (
              <Link
                key={item.href}
                to={item.href}
                data-testid={`mobile-nav-${item.href.replace("/", "")}`}
                className="text-white text-2xl font-medium py-3 border-b border-white/10"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <button
            onClick={goDiagnose}
            data-testid="mobile-header-cta"
            className="mt-8 inline-flex items-center justify-center gap-2 bg-coral text-white px-6 py-4 text-lg font-semibold"
            style={{ backgroundColor: "var(--coral)" }}
          >
            ابدأ تشخيص منظمتك
            <ArrowLeft size={18} />
          </button>
        </div>
      )}
    </header>
  );
}
